import { useEffect, useMemo, useState } from "react";
import {
  useListAttendanceSessions,
  useListAttendanceDevices,
  useCreateAttendanceDevice,
  useUpdateAttendanceDevice,
  useUpdateAttendanceSession,
  useCheckoutAttendanceSession,
  useImportAttendance,
  useResyncAttendance,
  useListAttendanceGeofences,
  useUpdateAttendanceGeofence,
  getListAttendanceSessionsQueryKey,
  getListAttendanceDevicesQueryKey,
  getListAttendanceGeofencesQueryKey,
  type AttendanceSession,
  type AttendanceDevice,
  type GeofenceSpace,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/auth-context";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ListPagination } from "@/components/common/list-pagination";
import { LogIn, LogOut, Upload, Plus, Pencil, MapPin, Loader2, RefreshCw } from "lucide-react";
import { fmtDay, fmtTime, fmtDuration, statusBadge } from "@/components/attendance/attendance-list";
import { DailyRoster } from "@/components/attendance/daily-roster";
import { PunchLog } from "@/components/attendance/punch-log";

// ── local date helpers (IST) ────────────────────────────────────────────────
const IST_OFFSET = "+05:30";
function ymdLocal(d: Date): string {
  return new Date(d.getTime() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
}
function thirtyDaysAgo(): string {
  return ymdLocal(new Date(Date.now() - 30 * 86_400_000));
}
function today(): string {
  return ymdLocal(new Date());
}
function dayStartISO(ymd: string): string {
  return new Date(`${ymd}T00:00:00${IST_OFFSET}`).toISOString();
}
function dayEndISO(ymd: string): string {
  return new Date(`${ymd}T23:59:59${IST_OFFSET}`).toISOString();
}
// ISO instant → "YYYY-MM-DDTHH:MM" for a datetime-local input, in IST.
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(new Date(iso).getTime() + 5.5 * 3600 * 1000).toISOString().slice(0, 16);
}
// datetime-local value (IST wall clock) → ISO instant.
function localInputToISO(v: string): string | null {
  if (!v) return null;
  const d = new Date(`${v}:00${IST_OFFSET}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export default function AttendancePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const isSuperAdmin = user?.role === "super_admin";
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState<"all" | AttendanceSession["status"]>("all");
  const [from, setFrom] = useState(thirtyDaysAgo);
  const [to, setTo] = useState(today);

  useEffect(() => {
    setPage(1);
  }, [status, from, to, pageSize]);

  const params = useMemo(
    () => ({
      ...(status !== "all" ? { status } : {}),
      ...(from ? { dateFrom: dayStartISO(from) } : {}),
      ...(to ? { dateTo: dayEndISO(to) } : {}),
      page,
      pageSize,
    }),
    [status, from, to, page, pageSize],
  );

  const { data, isLoading } = useListAttendanceSessions(params);
  const sessions = data?.items ?? [];
  const total = data?.total ?? 0;

  // "Currently inside" — count of open sessions (no exit yet).
  const { data: openData } = useListAttendanceSessions({ status: "open", pageSize: 1 });
  const insideNow = openData?.total ?? 0;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getListAttendanceSessionsQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListAttendanceDevicesQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListAttendanceGeofencesQueryKey() });
  };

  const [editing, setEditing] = useState<AttendanceSession | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [view, setView] = useState<"roster" | "sessions" | "punch">("roster");

  // Re-resolve taps that arrived before people were enrolled, then build sessions.
  const resync = useResyncAttendance();
  const doResync = async () => {
    try {
      const r = await resync.mutateAsync();
      toast({
        title: "Enrollments synced",
        description: r.matched > 0 ? `${r.matched} earlier tap(s) matched and turned into sessions.` : "No new taps to match.",
      });
      invalidate();
    } catch {
      toast({ title: "Sync failed", variant: "destructive" });
    }
  };

  // Reception check-out: read the device GPS, then close the session (server
  // geofence-verifies). A super-admin may override a block.
  const checkout = useCheckoutAttendanceSession();
  const [checkingOutId, setCheckingOutId] = useState<number | null>(null);

  const errText = (e: unknown): string => {
    const x = e as { data?: { error?: string }; message?: string } | null;
    return x?.data?.error ?? x?.message ?? "Something went wrong.";
  };
  const getPosition = (): Promise<{ lat: number; lng: number } | null> =>
    new Promise((resolve) => {
      if (!("geolocation" in navigator)) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
      );
    });
  const doCheckout = async (s: AttendanceSession) => {
    setCheckingOutId(s.id);
    try {
      const pos = await getPosition();
      try {
        await checkout.mutateAsync({ id: s.id, data: { lat: pos?.lat ?? null, lng: pos?.lng ?? null } });
        toast({ title: "Checked out", description: `${s.contactName ?? s.userName ?? "Person"} checked out.` });
        invalidate();
      } catch (e) {
        const msg = errText(e);
        if (isSuperAdmin && window.confirm(`${msg}\n\nForce check-out anyway (super-admin override)?`)) {
          await checkout.mutateAsync({ id: s.id, data: { override: true } });
          toast({ title: "Checked out (override)" });
          invalidate();
        } else {
          toast({ title: "Check-out blocked", description: msg, variant: "destructive" });
        }
      }
    } finally {
      setCheckingOutId(null);
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <LogIn className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-semibold">Attendance</h1>
        </div>
        {isAdmin ? (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={doResync} disabled={resync.isPending}>
              {resync.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}
              Sync enrollments
            </Button>
            <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
              <Upload className="mr-1.5 h-4 w-4" /> Import CSV
            </Button>
          </div>
        ) : null}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Currently inside</p>
            <p className="text-2xl font-semibold tabular-nums">{insideNow}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Sessions in range</p>
            <p className="text-2xl font-semibold tabular-nums">{total}</p>
          </CardContent>
        </Card>
      </div>

      {/* View toggle: daily register vs raw sessions log */}
      <div className="flex w-fit gap-1 rounded-md border bg-card p-1">
        <Button variant={view === "roster" ? "default" : "ghost"} size="sm" onClick={() => setView("roster")}>
          Daily roster
        </Button>
        <Button variant={view === "sessions" ? "default" : "ghost"} size="sm" onClick={() => setView("sessions")}>
          Sessions log
        </Button>
        <Button variant={view === "punch" ? "default" : "ghost"} size="sm" onClick={() => setView("punch")}>
          Punch log
        </Button>
      </div>

      {view === "roster" ? <DailyRoster /> : null}
      {view === "punch" ? <PunchLog /> : null}

      {view === "sessions" ? (
      <>
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3 rounded-md border bg-card px-3 py-2.5">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Date range</p>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              aria-label="From date"
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
              className="h-8 w-[150px]"
            />
            <span className="text-xs text-muted-foreground">→</span>
            <Input
              type="date"
              aria-label="To date"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
              className="h-8 w-[150px]"
            />
          </div>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Status</p>
          <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
            <SelectTrigger className="h-8 w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Inside (open)</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
              <SelectItem value="anomalous">Needs check</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Sessions table */}
      <Card>
        <CardContent className="pt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Person</TableHead>
                <TableHead>Type / Account</TableHead>
                <TableHead>Entry</TableHead>
                <TableHead>Exit</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[160px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : sessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                    No attendance in this range.
                  </TableCell>
                </TableRow>
              ) : (
                sessions.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>{fmtDay(s.sessionDate)}</TableCell>
                    <TableCell>{s.contactName ?? s.userName ?? "—"}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {s.personType === "staff" ? (
                        <Badge variant="outline">Staff</Badge>
                      ) : (
                        s.accountName ?? "—"
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums">{fmtTime(s.entryAt)}</TableCell>
                    <TableCell className="tabular-nums">{fmtTime(s.exitAt)}</TableCell>
                    <TableCell className="tabular-nums">{fmtDuration(s.durationMinutes)}</TableCell>
                    <TableCell>{statusBadge(s.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {s.status === "open" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7"
                            disabled={checkingOutId === s.id}
                            onClick={() => doCheckout(s)}
                          >
                            {checkingOutId === s.id ? (
                              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <LogOut className="mr-1 h-3.5 w-3.5" />
                            )}
                            Check out
                          </Button>
                        ) : null}
                        {isAdmin ? (
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(s)} aria-label="Edit session">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {!isLoading && total > 0 ? (
            <ListPagination
              total={total}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              noun={["session", "sessions"]}
            />
          ) : null}
        </CardContent>
      </Card>
      </>
      ) : null}

      {/* Geofence + Devices (admin) */}
      {isAdmin ? <GeofenceCard onChanged={invalidate} /> : null}
      {isAdmin ? <DevicesCard onChanged={invalidate} /> : null}

      {editing ? (
        <EditSessionDialog
          session={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            invalidate();
            setEditing(null);
          }}
        />
      ) : null}

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={invalidate} />
    </div>
  );
}

// ── Geofence config (per location) ──────────────────────────────────────────
function GeofenceCard({ onChanged }: { onChanged: () => void }) {
  const { data: spaces } = useListAttendanceGeofences();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Check-out geofence</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Reception can only check people out while physically on-site. For each location, stand at
          the entrance, tap <span className="font-medium">Use current location</span>, set a radius
          (e.g. 150 m), and Save.
        </p>
        {(spaces ?? []).length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">No locations.</p>
        ) : (
          (spaces ?? []).map((sp) => <GeofenceRow key={sp.spaceId} space={sp} onSaved={onChanged} />)
        )}
      </CardContent>
    </Card>
  );
}

function GeofenceRow({ space, onSaved }: { space: GeofenceSpace; onSaved: () => void }) {
  const { toast } = useToast();
  const update = useUpdateAttendanceGeofence();
  const [lat, setLat] = useState(space.geoLat != null ? String(space.geoLat) : "");
  const [lng, setLng] = useState(space.geoLng != null ? String(space.geoLng) : "");
  const [radius, setRadius] = useState(space.geoRadiusM != null ? String(space.geoRadiusM) : "150");
  const [locating, setLocating] = useState(false);

  const useCurrent = () => {
    if (!("geolocation" in navigator)) {
      toast({ title: "This device has no GPS/location", variant: "destructive" });
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setLat(p.coords.latitude.toFixed(6));
        setLng(p.coords.longitude.toFixed(6));
        setLocating(false);
        toast({ title: "Location captured", description: `±${Math.round(p.coords.accuracy)} m accuracy` });
      },
      () => {
        setLocating(false);
        toast({ title: "Couldn't get location", description: "Allow location access and retry.", variant: "destructive" });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const save = async () => {
    const latN = lat.trim() ? Number(lat) : null;
    const lngN = lng.trim() ? Number(lng) : null;
    const radN = radius.trim() ? Number(radius) : null;
    if ((latN == null) !== (lngN == null)) {
      toast({ title: "Enter both latitude and longitude", variant: "destructive" });
      return;
    }
    try {
      await update.mutateAsync({ spaceId: space.spaceId, data: { geoLat: latN, geoLng: lngN, geoRadiusM: radN } });
      toast({ title: "Geofence saved" });
      onSaved();
    } catch {
      toast({ title: "Save failed", variant: "destructive" });
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-2 border-t pt-3">
      <div className="min-w-[140px] pb-1.5 text-sm font-medium">{space.spaceName}</div>
      <div className="space-y-1">
        <Label className="text-xs">Latitude</Label>
        <Input value={lat} onChange={(e) => setLat(e.target.value)} placeholder="28.612900" className="h-8 w-[130px]" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Longitude</Label>
        <Input value={lng} onChange={(e) => setLng(e.target.value)} placeholder="77.041700" className="h-8 w-[130px]" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Radius (m)</Label>
        <Input value={radius} onChange={(e) => setRadius(e.target.value)} className="h-8 w-[90px]" />
      </div>
      <Button variant="outline" size="sm" onClick={useCurrent} disabled={locating}>
        {locating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <MapPin className="mr-1 h-4 w-4" />}
        Use current location
      </Button>
      <Button size="sm" onClick={save} disabled={update.isPending}>Save</Button>
    </div>
  );
}

// ── Devices management card ─────────────────────────────────────────────────
function DevicesCard({ onChanged }: { onChanged: () => void }) {
  const { toast } = useToast();
  const { data: devices } = useListAttendanceDevices();
  const createDevice = useCreateAttendanceDevice();
  const updateDevice = useUpdateAttendanceDevice();

  const [serial, setSerial] = useState("");
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [mode, setMode] = useState("entry_only");

  const register = async () => {
    if (!serial.trim() || !name.trim()) {
      toast({ title: "Serial and name are required", variant: "destructive" });
      return;
    }
    try {
      await createDevice.mutateAsync({
        data: { serialNumber: serial.trim(), name: name.trim(), location: location.trim() || null, mode },
      });
      toast({ title: "Device registered" });
      setSerial("");
      setName("");
      setLocation("");
      setMode("entry_only");
      onChanged();
    } catch {
      toast({ title: "Could not register device", description: "Serial may already exist.", variant: "destructive" });
    }
  };

  const toggleActive = async (d: AttendanceDevice) => {
    try {
      await updateDevice.mutateAsync({ id: d.id, data: { isActive: !d.isActive } });
      onChanged();
    } catch {
      toast({ title: "Update failed", variant: "destructive" });
    }
  };

  const changeMode = async (d: AttendanceDevice, nextMode: string) => {
    try {
      await updateDevice.mutateAsync({ id: d.id, data: { mode: nextMode } });
      onChanged();
    } catch {
      toast({ title: "Update failed", variant: "destructive" });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Biometric devices</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Register each eSSL device by its <span className="font-medium">serial number</span> (Menu →
          System Info). On the device set <span className="font-medium">Comm → Cloud Server / ADMS</span>{" "}
          → Server Address = this app's API domain, then enable it. Only registered devices are accepted.
        </p>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Serial</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead>Last seen</TableHead>
              <TableHead>Last punch</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {(devices ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-4 text-center text-sm text-muted-foreground">
                  No devices registered yet.
                </TableCell>
              </TableRow>
            ) : (
              (devices ?? []).map((d) => (
                <TableRow key={d.id}>
                  <TableCell className="font-mono text-xs">{d.serialNumber}</TableCell>
                  <TableCell>{d.name}</TableCell>
                  <TableCell>
                    <Select value={d.mode} onValueChange={(v) => changeMode(d, v)}>
                      <SelectTrigger className="h-7 w-[160px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="entry_only">Entry-only (check-in)</SelectItem>
                        <SelectItem value="entry_exit">Entry/Exit (pairs)</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {d.lastSeenAt ? `${fmtDay(d.lastSeenAt.slice(0, 10))} ${fmtTime(d.lastSeenAt)}` : "never"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {d.lastPunchAt ? `${fmtDay(d.lastPunchAt.slice(0, 10))} ${fmtTime(d.lastPunchAt)}` : "—"}
                  </TableCell>
                  <TableCell>
                    {d.isActive ? <Badge>Active</Badge> : <Badge variant="secondary">Disabled</Badge>}
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => toggleActive(d)}>
                      {d.isActive ? "Disable" : "Enable"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <div className="flex flex-wrap items-end gap-2 border-t pt-3">
          <div className="space-y-1">
            <Label className="text-xs">Serial number</Label>
            <Input value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="e.g. CGXM214860001" className="h-8 w-[180px]" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Front desk" className="h-8 w-[150px]" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Location</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Reception" className="h-8 w-[150px]" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="h-8 w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="entry_only">Entry-only (check-in)</SelectItem>
                <SelectItem value="entry_exit">Entry/Exit (pairs)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" onClick={register} disabled={createDevice.isPending}>
            <Plus className="mr-1.5 h-4 w-4" /> Register
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Edit / correct a session (admin) ────────────────────────────────────────
function EditSessionDialog({
  session,
  onClose,
  onSaved,
}: {
  session: AttendanceSession;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const update = useUpdateAttendanceSession();
  const [entry, setEntry] = useState(toLocalInput(session.entryAt));
  const [exit, setExit] = useState(toLocalInput(session.exitAt));
  const [note, setNote] = useState(session.note ?? "");

  const save = async () => {
    const entryISO = localInputToISO(entry);
    if (!entryISO) {
      toast({ title: "Entry time is required", variant: "destructive" });
      return;
    }
    try {
      await update.mutateAsync({
        id: session.id,
        data: {
          entryAt: entryISO,
          exitAt: exit ? localInputToISO(exit) : null,
          note: note.trim() || null,
        },
      });
      toast({ title: "Session updated" });
      onSaved();
    } catch {
      toast({ title: "Update failed", variant: "destructive" });
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Correct session — {session.contactName ?? `Contact #${session.contactId}`}</DialogTitle>
          <DialogDescription>
            Edits are saved as a manual override and won't be overwritten by future device syncs.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Entry</Label>
            <Input type="datetime-local" value={entry} onChange={(e) => setEntry(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Exit (leave empty if still inside)</Label>
            <Input type="datetime-local" value={exit} onChange={(e) => setExit(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Note</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. forgot to punch out" rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={update.isPending}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── CSV import (admin) ──────────────────────────────────────────────────────
// Accepts comma- or tab-separated rows: PIN, "YYYY-MM-DD HH:MM:SS"[, status].
// A header row is auto-skipped when its 2nd column isn't a parseable datetime.
function parseCsv(text: string): Array<{ pin: string; time: string; status?: number }> {
  const rows: Array<{ pin: string; time: string; status?: number }> = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const cols = line.split(/[,\t]/).map((c) => c.trim().replace(/^"|"$/g, ""));
    const pin = cols[0] ?? "";
    const time = cols[1] ?? "";
    if (!pin || !time) continue;
    // skip a header line (col1 not a date)
    if (!/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(time)) continue;
    const statusRaw = cols[2];
    const status = statusRaw != null && statusRaw !== "" && !Number.isNaN(Number(statusRaw)) ? Number(statusRaw) : undefined;
    rows.push({ pin, time, ...(status != null ? { status } : {}) });
  }
  return rows;
}

function ImportDialog({
  open,
  onClose,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}) {
  const { toast } = useToast();
  const importMut = useImportAttendance();
  const [text, setText] = useState("");
  const [serial, setSerial] = useState("");

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setText(await file.text());
  };

  const submit = async () => {
    const rows = parseCsv(text);
    if (rows.length === 0) {
      toast({ title: "No valid rows found", description: "Expected: PIN, YYYY-MM-DD HH:MM:SS [, status]", variant: "destructive" });
      return;
    }
    try {
      const res = await importMut.mutateAsync({
        data: { deviceSerial: serial.trim() || null, rows },
      });
      toast({
        title: "Import complete",
        description: `${res.inserted} added · ${res.duplicates} duplicates · ${res.unresolved} unmatched PIN(s)`,
      });
      setText("");
      onImported();
      onClose();
    } catch {
      toast({ title: "Import failed", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import attendance (CSV)</DialogTitle>
          <DialogDescription>
            From an eTimeTrackLite export. Each row: <span className="font-mono">PIN, YYYY-MM-DD HH:MM:SS</span>{" "}
            (optionally a 3rd status column). PIN must match a member's Biometric ID.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Device serial (optional)</Label>
            <Input value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="links rows to a registered device" className="h-8" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">CSV file</Label>
            <Input type="file" accept=".csv,.txt,text/csv" onChange={(e) => onFile(e.target.files?.[0])} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">…or paste rows</Label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} placeholder={"1001, 2026-06-22 09:15:00\n1002, 2026-06-22 09:20:00, 0"} className="font-mono text-xs" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={importMut.isPending}>Import</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
